/**
 * Referral Code Tracking Utility
 * Captures and persists referral codes with 10-minute expiration
 */

const REFERRAL_STORAGE_KEY = "illumi-referral-code";
const REFERRAL_EXPIRY_KEY = "illumi-referral-expiry";
const REFERRAL_EXPIRY_MINUTES = 10; // Referral code expires after 10 minutes

export interface ReferralData {
	code: string;
	timestamp: string;
}

/**
 * Extract referral code from URL search params
 */
export function extractReferralCode(
	searchParams: URLSearchParams,
): string | null {
	// Check for common referral parameter names
	const referralParamNames = ["ref", "referral", "referral_code", "refCode"];

	for (const paramName of referralParamNames) {
		const value = searchParams.get(paramName);
		if (value) {
			return value;
		}
	}

	return null;
}

/**
 * Check if stored referral code has expired
 */
function isReferralExpired(): boolean {
	const expiryStr = localStorage.getItem(REFERRAL_EXPIRY_KEY);
	if (!expiryStr) return true;

	const expiryDate = new Date(expiryStr);
	return new Date() > expiryDate;
}

/**
 * Save referral code to localStorage
 */
export function saveReferralCode(code: string): void {
	if (!code) return;

	const referralData: ReferralData = {
		code,
		timestamp: new Date().toISOString(),
	};

	// Set expiry date (10 minutes from now)
	const expiryDate = new Date();
	expiryDate.setMinutes(expiryDate.getMinutes() + REFERRAL_EXPIRY_MINUTES);

	localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));
	localStorage.setItem(REFERRAL_EXPIRY_KEY, expiryDate.toISOString());
}

/**
 * Get stored referral code from localStorage
 */
export function getStoredReferralCode(): string | null {
	if (isReferralExpired()) {
		clearReferralCode();
		return null;
	}

	const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
	if (!stored) return null;

	try {
		const referralData = JSON.parse(stored) as ReferralData;
		return referralData.code;
	} catch (error) {
		console.error("Error parsing stored referral code:", error);
		clearReferralCode();
		return null;
	}
}

/**
 * Clear stored referral code
 */
export function clearReferralCode(): void {
	localStorage.removeItem(REFERRAL_STORAGE_KEY);
	localStorage.removeItem(REFERRAL_EXPIRY_KEY);
}

/**
 * Capture referral code from current URL if present
 * Always overwrites existing referral code (last-touch attribution)
 */
export function captureReferralCode(): string | null {
	const searchParams = new URLSearchParams(window.location.search);
	const referralCode = extractReferralCode(searchParams);

	// If we found a referral code in the URL, save it (overwriting any existing one)
	if (referralCode) {
		saveReferralCode(referralCode);
		return referralCode;
	}

	// Otherwise, return any stored referral code if it hasn't expired
	return getStoredReferralCode();
}
